/* An independent, single-item add-on: never submits the main product or Customily form. */
(() => {
  if (customElements.get('byards-santa-key')) return;

  const cards = new Set();
  let busy = false;
  let syncTimer;
  let syncVersion = 0;
  let refreshAfterBusy = false;

  const cartUrl = (card) => card.dataset.cartUrl || window.theme?.routes?.cart || '/cart';
  const cartContains = (cart, productId) => cart.items.some(
    (item) => String(item.product_id) === String(productId)
  );

  async function readCart(card) {
    const url = new URL(cartUrl(card), window.location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    const response = await fetch(url.href, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read basket');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Invalid basket response');
    return cart;
  }

  function setBusy(value) {
    busy = value;
    cards.forEach((card) => card.setBusy(value));
  }

  function queueSync(event) {
    if (event?.detail?.byardsSantaKey) return;
    if (busy) {
      refreshAfterBusy = true;
      return;
    }
    clearTimeout(syncTimer);
    syncTimer = setTimeout(async () => {
      if (busy) {
        refreshAfterBusy = true;
        return;
      }
      const card = cards.values().next().value;
      if (!card) return;
      const version = ++syncVersion;
      try {
        const cart = await readCart(card);
        if (version !== syncVersion || busy) return;
        cards.forEach((item) => item.setPresence(cartContains(cart, item.dataset.productId)));
      } catch (error) {
        // Leave the server-rendered offer intact; submission always checks again.
      }
    }, 100);
  }

  class ByardsSantaKey extends HTMLElement {
    connectedCallback() {
      if (this.boundSubmit) return;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-santa-add]');
      this.label = this.querySelector('[data-santa-label]');
      this.status = this.querySelector('[data-santa-status]');
      this.error = this.querySelector('[data-santa-error]');
      this.offer = this.querySelector('[data-santa-offer]');
      this.added = this.querySelector('[data-santa-added]');
      if (!this.form || !this.button || !this.offer || !this.added) return;
      this.originalLabel = this.label?.textContent;
      this.originalDisabled = this.button.disabled;
      this.boundSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.boundSubmit);
      cards.add(this);
      this.setBusy(busy);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.boundSubmit);
      this.boundSubmit = null;
      cards.delete(this);
    }

    setBusy(value) {
      this.button.disabled = value || this.originalDisabled;
    }

    setError(message = '') {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    showAdded(message, moveFocus = false) {
      this.hidden = false;
      this.added.hidden = false;
      const messageNode = this.added.querySelector('[data-santa-added-message]');
      if (messageNode) messageNode.textContent = message;
      if (this.status) this.status.textContent = message;
      this.setError();
      // Move focus before hiding the clicked button, including when no drawer is used.
      if (moveFocus || this.offer.contains(document.activeElement)) {
        (this.added.querySelector('a[href]') || this.status)?.focus({ preventScroll: true });
      }
      this.offer.hidden = true;
    }

    setPresence(present) {
      if (present) {
        if (this.contains(document.activeElement)) {
          this.showAdded('Santa’s key is already in your basket.', true);
        } else {
          this.hidden = true;
        }
        return;
      }
      this.hidden = false;
      this.offer.hidden = false;
      if (this.added.contains(document.activeElement)) {
        this.button.focus({ preventScroll: true });
      }
      this.added.hidden = true;
      if (this.status) this.status.textContent = '';
      this.setError();
    }

    async updateCartUI(data, drawer, afterAtc) {
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleHTML = data.sections?.['cart-icon-bubble'];
      if (bubble && typeof bubbleHTML === 'string') bubble.innerHTML = bubbleHTML;

      if (drawer) {
        const sections = drawer.getSectionsToRender?.() || [];
        const hasSections = sections.length && sections.every(
          (section) => typeof data.sections?.[section.section] === 'string'
        );
        if (hasSections && typeof drawer.renderContents === 'function') {
          // Open separately so closing the drawer restores focus to the visible basket link.
          await drawer.renderContents(data, false);
        } else if (typeof drawer.refresh === 'function') {
          await drawer.refresh(true);
        }
        if (afterAtc === 'drawer' && typeof drawer.open === 'function') {
          const opener = this.added.querySelector('a[href]');
          if (!drawer.hasAttribute('open')) drawer.open(opener);
        }
      } else if (window.location.pathname === cartUrl(this)) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems?.dataset.empty === 'true') window.location.reload();
        else if (typeof cartItems?.refresh === 'function') await cartItems.refresh();
      }
    }

    async handleSubmit(event) {
      const afterAtc = window.theme?.settings?.afterAtc;
      // Keep the ordinary Shopify form usable when JavaScript cart handling is disabled.
      if (afterAtc === 'no-js') return;
      event.preventDefault();
      event.stopPropagation();
      if (busy || this.originalDisabled) return;
      const startedWithFocus = this.contains(document.activeElement);

      ++syncVersion; // Ignore any passive cart read started before this submission.
      clearTimeout(syncTimer);
      setBusy(true);
      this.setAttribute('aria-busy', 'true');
      this.setError();
      if (this.status) this.status.textContent = 'Adding Santa’s key…';
      if (this.label) this.label.textContent = 'Adding…';

      let postStarted = false;
      let addConfirmed = false;
      try {
        const cart = await readCart(this);
        if (cartContains(cart, this.dataset.productId)) {
          cards.forEach((card) => {
            if (card !== this) card.setPresence(cartContains(cart, card.dataset.productId));
          });
          this.showAdded('Santa’s key is already in your basket.', true);
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        const drawerSection = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
        const sections = ['cart-icon-bubble', drawerSection].filter(Boolean);
        const formData = new FormData(this.form);
        formData.set('id', this.dataset.variantId);
        formData.set('quantity', '1');
        formData.set('sections', [...new Set(sections)].join(','));
        formData.set('sections_url', window.location.pathname);

        postStarted = true;
        const response = await fetch(this.form.action, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = typeof data.description === 'string'
            ? data.description : (typeof data.message === 'string' ? data.message : 'Unable to add Santa’s key. Please try again.');
          this.setError(message);
          if (this.status) this.status.textContent = '';
          return;
        }
        addConfirmed = true;
        cards.forEach((card) => {
          if (card !== this && card.dataset.productId === this.dataset.productId) card.setPresence(true);
        });
        this.showAdded('Santa’s key has been added to your basket.', true);
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: Number(this.dataset.variantId), byardsSantaKey: true }
        }));

        if (afterAtc === 'page') {
          window.location.assign(cartUrl(this));
          return;
        }
        await this.updateCartUI(data, drawer, afterAtc);
      } catch (error) {
        if (addConfirmed) {
          // The item was added even if section rendering failed; keep the basket link available.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        } else {
          if (this.status) this.status.textContent = '';
          this.setError(postStarted
            ? 'We couldn’t confirm the update. Please check your basket before trying again.'
            : 'We couldn’t check your basket. Please try again.');
        }
      } finally {
        this.removeAttribute('aria-busy');
        if (this.label) this.label.textContent = this.originalLabel;
        setBusy(false);
        if (startedWithFocus && !this.offer.hidden && document.activeElement === document.body) {
          this.button.focus({ preventScroll: true });
        }
        if (refreshAfterBusy) {
          refreshAfterBusy = false;
          queueSync();
        }
      }
    }
  }

  customElements.define('byards-santa-key', ByardsSantaKey);
  ['on:cart:add', 'on:line-item:change', 'on:bfcache:load-restore'].forEach((eventName) => {
    document.addEventListener(eventName, queueSync);
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) queueSync(); });
  window.addEventListener('focus', () => queueSync());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') queueSync();
  });
})();
